import { ref, computed, watch, Ref, SetupContext } from '@vue/composition-api'
import * as d3 from 'd3'
import { debounce } from '../utils'
import {
  Size,
  ComponentProps,
  AllPropsD3Module,
  D3ModuleConstructable } from '@bpchart/types'

type EventCallback = (eventName: string, data: any) => void

type UseD3ModuleParams<T> = {
  D3Module: D3ModuleConstructable<T>;
  props: ComponentProps;
  context: SetupContext;
}

type UseD3ModuleReturn<T> = 
{
  d3Module: Ref<T>
  createComponent: (el: SVGElement) => void
  eventHandler: (callback: EventCallback) => void
}

export default function useD3Module<T = AllPropsD3Module> ({ D3Module, props, context }: UseD3ModuleParams<T>) {

  const d3Module: Ref<T | AllPropsD3Module | undefined> = ref(undefined)

  const refEl = ref()

  // render
  const render = () => {
    if (d3Module.value && (d3Module.value as AllPropsD3Module).render) {
      ;(d3Module.value as AllPropsD3Module).render()
      // console.log('render')
    }
  }
  const debounceRender = debounce(render)

  const setParams = (value: any) => value
    && (d3Module.value as AllPropsD3Module).setParams
    && (d3Module.value as AllPropsD3Module).setParams!(value)
  const setData = (value: any) => value
    && (d3Module.value as AllPropsD3Module).setData
    && (d3Module.value as AllPropsD3Module).setData(value)
  const setDataset = (value: any) => value
    && (d3Module.value as AllPropsD3Module).setDataset
    && (d3Module.value as AllPropsD3Module).setDataset(value)
  const setDatum = (value: any) => value
    && (d3Module.value as AllPropsD3Module).setDatum
    && (d3Module.value as AllPropsD3Module).setDatum(value)
  const filter = (value: any) => value
    && (d3Module.value as AllPropsD3Module).filter
    && (d3Module.value as AllPropsD3Module).filter!(value)
  const resize = ({ width, height }: any) => {
    (d3Module.value as AllPropsD3Module).resize
    && (d3Module.value as AllPropsD3Module).resize({
      width: width && typeof width === 'number' ? width : (d3Module.value as AllPropsD3Module).width,
      height: height && typeof height === 'number' ? height : (d3Module.value as AllPropsD3Module).height
    })
  }
  const zoom = (value: any) => {
    if (value
      && (d3Module.value as AllPropsD3Module).zoom
      && (d3Module.value as AllPropsD3Module).transform
      && (d3Module.value as AllPropsD3Module).transformZoom
      && (d3Module.value as AllPropsD3Module).initZoom
    ) {
      ;(d3Module.value as AllPropsD3Module).initZoom((d3Module.value as AllPropsD3Module).zoom)
      ;(d3Module.value as AllPropsD3Module).transformZoom(value)
    }
  }
  watch(() => props.params, (value) => {
    setParams(value || {})
  })
  watch(() => props.data, (value) => {
    setData(value)
  })
  watch(() => props.dataset, (value) => {
    setDataset(value)
  })
  watch(() => props.datum, (value) => {
    setDatum(value)
  })
  watch(() => props.filter, (value) => {
    filter(value)
  })
  watch(() => [props.width, props.height], ({ width, height }: any) => {
    (props.width || props.height ) && resize({ width, height })
  })
  watch(() => props.zoom, (value) => {
    zoom(value)
  })
  // watch(() => [props.params, props.data, props.dataset, props.datum, props.filter, props.width, props.height, props.forceFit, props.zoom], () => {
  //   debounceRender()
  // })

  return {
    d3Module: d3Module as Ref<T>,
    debounceRender,
    createModule (el: SVGElement) {
      refEl.value = el
      d3Module.value = new D3Module(d3.select(el), props.params || {})
      // console.log('filter', props.filter)
      setParams(props.params || {})
      setData(props.data)
      setDataset(props.dataset)
      setDatum(props.datum)
      filter(props.filter)
      resize({
        width: props.width,
        height: props.height
      });
      zoom(props.zoom)

    },
    eventHandler (callback: EventCallback) {
      // 事件處理
      let eventCallback: EventCallback = callback
      // @Q@ listeners是 vue2取得 v-on事件的寫法，vue3將放到 attr中
      Object.keys(context.listeners).forEach(key => {
        if ((d3Module.value as AllPropsD3Module).on != undefined) {
          (d3Module.value as AllPropsD3Module)!.on(key as any, (d: any) => {
            if (eventCallback) {
              eventCallback(key, d)
            }
          })
        }
      })
    }
  }
}
